import type { MergeFieldContext, PartnerTier, PartnerType } from "./domain";

// Merge-field rendering for portal content. Substitutes `{partner.first_name}`-
// style tokens at view time with values from the resolved partner context.
//
// Lifted from the legacy /lib/merge-fields.ts into @colab/shared so both
// apps/api (server-side rendering, email/notification flows) and apps/web
// (portal dashboard banner, FAQ, level-up widget) consume one source of
// truth without crossing the workspace boundary.

export const MERGE_FIELD_TOKENS = [
  "partner.first_name",
  "partner.last_name",
  "partner.company_name",
  "partner.tier",
  "partner.partner_type",
] as const;

export type MergeFieldToken = (typeof MERGE_FIELD_TOKENS)[number];

export const MERGE_FIELD_LABELS: Record<MergeFieldToken, string> = {
  "partner.first_name": "Partner first name",
  "partner.last_name": "Partner last name",
  "partner.company_name": "Partner company name",
  "partner.tier": "Partner tier",
  "partner.partner_type": "Partner type",
};

const TOKEN_PATTERN = /\{\s*([a-z_]+(?:\.[a-z_]+)+)\s*\}/g;

// Unknown tokens fall back to "" (not the raw token) so a typo in admin-
// authored content surfaces as an empty value in the editor preview rather
// than leaking `{partner.bogus_field}` to the portal.
export function renderMergeFields(
  template: string | null | undefined,
  ctx: MergeFieldContext,
): string {
  if (!template) return "";
  return template.replace(TOKEN_PATTERN, (_match, token: string) => {
    const value = resolveToken(token, ctx);
    return value ?? "";
  });
}

function resolveToken(token: string, ctx: MergeFieldContext): string | null {
  switch (token) {
    case "partner.first_name":
      return ctx.partner.first_name;
    case "partner.last_name":
      return ctx.partner.last_name;
    case "partner.company_name":
      return ctx.partner.company_name;
    case "partner.tier":
      return ctx.partner.tier;
    case "partner.partner_type":
      return ctx.partner.partner_type;
    default:
      return null;
  }
}

export function extractMergeFieldTokens(template: string | null | undefined): string[] {
  if (!template) return [];
  const seen = new Set<string>();
  for (const match of template.matchAll(TOKEN_PATTERN)) {
    // match[1] is `string | undefined` under noUncheckedIndexedAccess (the
    // e2e workspace's tsconfig sets it). The regex has exactly one capture
    // group + we only iterate when `template` matched, so the capture is
    // always present in practice — but the type still needs a runtime guard.
    const captured = match[1];
    if (captured !== undefined) seen.add(captured);
  }
  return Array.from(seen);
}

export function isKnownMergeFieldToken(token: string): token is MergeFieldToken {
  return (MERGE_FIELD_TOKENS as readonly string[]).includes(token);
}

export type PartnerForMergeFields = {
  name: string | null;
  tier: PartnerTier | null;
  partner_type: PartnerType | null;
};

export function buildMergeFieldContext(args: {
  partner: PartnerForMergeFields;
  partnerUserFullName?: string | null;
}): MergeFieldContext {
  const fullName = args.partnerUserFullName ?? null;
  const [firstName, ...restName] = (fullName ?? "").trim().split(/\s+/);
  return {
    partner: {
      first_name: firstName || null,
      last_name: restName.length > 0 ? restName.join(" ") : null,
      company_name: args.partner.name,
      tier: args.partner.tier,
      partner_type: args.partner.partner_type,
    },
  };
}
